import { JitsiLocalTrack, JitsiMediaDeviceInfo } from "lib/lib-jitsi-meet";
import { action, flow, reaction, runInAction } from "mobx";
import { MediaDevicesState, persistSetting } from "../../common";
import AbstractDataComponent from "./AbstractDataComponent";
import { JitsiManagerChildProps } from "./JitsiManager";

const mediaConstraints = {
    video: {
        aspectRatio: 16 / 9,
        height: {
            ideal: 360,
            max: 360,
            min: 240,
        },
        frameRate: {
            max: 24,
        },
    },
};

export interface MediaDevicesManagerProps extends JitsiManagerChildProps {
    mediaDevicesState: MediaDevicesState;
}

export default class MediaDevicesManager extends AbstractDataComponent<MediaDevicesManagerProps> {
    public static displayName = "MediaDevicesManager";

    public static sortAndUpdateMediaDevices = (state: MediaDevicesState, devices: JitsiMediaDeviceInfo[]) => {
        // TODO: narrow types
        const audioInputs: JitsiMediaDeviceInfo[] = [];
        const audioOutputs: JitsiMediaDeviceInfo[] = [];
        const videoInputs: JitsiMediaDeviceInfo[] = [];

        for (const device of devices) {
            if (device.kind === "audioinput") {
                audioInputs.push(device);
            } else if (device.kind === "videoinput") {
                videoInputs.push(device);
            } else {
                audioOutputs.push(device);
            }
        }

        state.audioInputs = audioInputs;
        state.audioOutputs = audioOutputs;
        state.videoInputs = videoInputs;
    };

    public async componentDidMount() {
        const { jitsi, mediaDevicesState } = this.props;

        const granted = await jitsi.mediaDevices.isDevicePermissionGranted();
        if (!granted) {
            runInAction(() => (mediaDevicesState.shouldReEnumerateDevices = true));
        }

        jitsi.mediaDevices.addEventListener(
            jitsi.events.mediaDevices.DEVICE_LIST_CHANGED,
            this.handleDeviceListChanged
        );

        reaction(
            () => mediaDevicesState.audioInputDeviceId,
            (id: string) => {
                this.createLocalAudioTrack(id);
                persistSetting("audioInputDeviceId", id);
            }
        );

        reaction(
            () => mediaDevicesState.videoInputDeviceId,
            (id: string) => {
                this.createLocalVideoTrack(id);
                persistSetting("videoInputDeviceId", id);
            }
        );

        // fetch initial list of devices
        if (jitsi.mediaDevices.isDeviceListAvailable()) {
            jitsi.mediaDevices.enumerateDevices(this.sortAndUpdateMediaDevices);
            this.createInitialTracks();
        }
    }

    public componentWillUnmount() {
        const { jitsi, mediaDevicesState } = this.props;

        jitsi.mediaDevices.removeEventListener(
            jitsi.events.mediaDevices.DEVICE_LIST_CHANGED,
            this.handleDeviceListChanged
        );

        // TODO: restore or delete this code
        // mediaDevicesState.audioInputTrack?.dispose();
        // mediaDevicesState.videoInputTrack?.dispose();
    }

    @action
    private sortAndUpdateMediaDevices = (devices: JitsiMediaDeviceInfo[]) => {
        MediaDevicesManager.sortAndUpdateMediaDevices(this.props.mediaDevicesState, devices);
    };

    private handleDeviceListChanged = (devices: JitsiMediaDeviceInfo[]) => {
        this.sortAndUpdateMediaDevices(devices);
        console.info("Local media devices changed");
    };

    /**
     * Create both local tracks at once, upon initialization
     */
    private createInitialTracks = flow(function* (this: MediaDevicesManager) {
        const { jitsi, mediaDevicesState } = this.props;
        const { audioInputDeviceId, videoInputDeviceId } = mediaDevicesState;
        const tracks = yield this.props.jitsi.createLocalTracks({
            devices: ["video", "audio"],
            micDeviceId: audioInputDeviceId,
            cameraDeviceId: videoInputDeviceId,
            constraints: mediaConstraints,
        });

        if (mediaDevicesState.shouldReEnumerateDevices) {
            mediaDevicesState.shouldReEnumerateDevices = false;
            jitsi.mediaDevices.enumerateDevices((devices: MediaDeviceInfo[]) => {
                MediaDevicesManager.sortAndUpdateMediaDevices(mediaDevicesState, devices);
            });
        }

        tracks.forEach(this.updateInputTrack);
    });

    private createLocalAudioTrack = async (deviceId: string) => {
        const tracks = await this.props.jitsi.createLocalTracks({
            devices: ["audio"],
            micDeviceId: deviceId,
            constraints: mediaConstraints,
        });
        // action tracked here
        return this.updateInputTrack(tracks.find((t) => t.getType() === "audio"));
    };

    private createLocalVideoTrack = async (deviceId: string) => {
        const tracks = await this.props.jitsi.createLocalTracks({
            devices: ["video"],
            cameraDeviceId: deviceId,
            constraints: mediaConstraints,
        });
        // action tracked here
        return this.updateInputTrack(tracks.find((t) => t.getType() === "video"));
    };

    @action
    private updateInputTrack = (track?: JitsiLocalTrack) => {
        const { mediaDevicesState, room, isHost } = this.props;

        if (track === undefined) {
            return;
        } else if (track.getType() === "video") {
            const prev = mediaDevicesState.videoInputTrack;
            mediaDevicesState.videoInputTrack = track;
            room.replaceTrack(prev, track);
        } else if (track.getType() === "audio") {
            const prev = mediaDevicesState.audioInputTrack;
            mediaDevicesState.audioInputTrack = track;
            if (!isHost) {
                track.mute();
            }
            room.replaceTrack(prev, track);
        }
        return track;
    };
}
